import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2, ArrowRight, ShieldCheck, Award, Star, Wrench } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CtaBanner } from "./Home";
import { IMG, CONTACT, HOURS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGES = [
  { icon: Award, label: "35+ Years Experience" },
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Star, label: "ARCtick Certified" },
  { icon: Wrench, label: "10+ Premium Brands" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", project_type: "", project_location: "", services: "", preferred_contact: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — we'll get back to you shortly!");
      setForm({ name: "", company: "", email: "", phone: "", project_type: "", project_location: "", services: "", preferred_contact: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setSubmitting(false);
  };

  const inp = "w-full h-14 px-5 rounded-2xl bg-js-grey border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none transition-colors duration-200";
  const info = [
    { icon: Phone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    ...CONTACT.addresses.map((a) => ({ icon: MapPin, label: `${a.label} Office`, value: a.value })),
  ];

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="GET IN TOUCH" title="Let's talk comfort" subtitle="Questions or quotes — our friendly Melbourne team is ready to help." image={IMG.melbourneStreet} crumbs={[{ label: "Contact" }]} />

      {/* Trust strip */}
      <section className="bg-js-grey border-b border-black/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {BADGES.map((b) => (
            <div key={b.label} className="flex items-center gap-3">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-js-blue/10 text-js-blue shrink-0"><b.icon size={19} /></span>
              <span className="text-sm font-semibold text-js-charcoal/80">{b.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-2">Contact details</h2>
            <p className="text-js-charcoal/50 text-sm mb-6">J & S AIRCONDITIONING PTY LTD · ABN 84 055 063 548</p>
            <div className="space-y-4">
              {info.map((it) => (
                <div key={it.label} className="flex items-start gap-4 bg-js-grey rounded-2xl p-5 border border-black/5">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-js-blue text-white shrink-0"><it.icon size={20} /></span>
                  <div>
                    <p className="text-xs text-js-charcoal/50 font-semibold tracking-wide">{it.label.toUpperCase()}</p>
                    {it.href ? <a href={it.href} className="font-semibold text-js-charcoal hover:text-js-blue transition-colors">{it.value}</a> : <p className="font-semibold text-js-charcoal">{it.value}</p>}
                  </div>
                </div>
              ))}
              <div className="flex items-start gap-4 bg-js-grey rounded-2xl p-5 border border-black/5" data-testid="contact-hours">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-js-blue text-white shrink-0"><Clock size={20} /></span>
                <div className="w-full">
                  <p className="text-xs text-js-charcoal/50 font-semibold tracking-wide">OPENING HOURS</p>
                  <ul className="mt-2 space-y-1.5">
                    {HOURS.map((h) => (
                      <li key={h.day} className="flex items-center justify-between gap-4 text-sm">
                        <span className="text-js-charcoal/70">{h.day}</span>
                        <span className={`font-semibold ${h.time === "Closed" ? "text-js-red" : "text-js-charcoal"}`}>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-6 rounded-3xl overflow-hidden border border-black/5">
              <iframe title="J&S location" className="w-full h-64 grayscale" loading="lazy"
                src="https://www.google.com/maps?q=J%26S%20Air%20Conditioning%2C%2012%2F14%20Garden%20Dr%2C%20Tullamarine%20VIC%203043&output=embed" />
              <a href={CONTACT.googleListingUrl}
                target="_blank" rel="noopener noreferrer" data-testid="google-business-link"
                className="flex items-center justify-center gap-2 py-3.5 bg-js-blue text-white text-sm font-semibold hover:bg-js-dark transition-colors duration-300">
                <MapPin size={16} /> View us on Google Maps
              </a>
            </div>

            {/* Google Business card */}
            <div data-testid="google-business-card" className="mt-6 bg-white rounded-3xl p-6 border border-black/5 shadow-[0_8px_32px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-4">
                <svg width="40" height="40" viewBox="0 0 48 48" className="shrink-0" aria-hidden="true">
                  <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                  <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                  <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
                  <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                </svg>
                <div>
                  <p className="font-display font-bold text-js-charcoal leading-tight">J&amp;S Air Conditioning</p>
                  <p className="text-js-charcoal/50 text-xs mt-0.5">Find &amp; review us on Google Business</p>
                </div>
              </div>
              <p className="text-js-charcoal/60 text-sm mt-4 leading-relaxed">Been looked after by our team? We'd love a quick Google review — it helps other Melbourne locals find us.</p>
              <div className="flex flex-col gap-3 mt-5">
                <a href={CONTACT.googleListingUrl}
                  target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link"
                  className="flex items-center justify-center gap-2 py-3 rounded-full bg-js-grey border border-black/5 text-sm font-semibold text-js-charcoal hover:bg-js-blue/5 hover:text-js-blue transition-colors duration-300">
                  <MapPin size={15} /> View our Google Listing
                </a>
                <a href={CONTACT.googleReviewUrl}
                  target="_blank" rel="noopener noreferrer" data-testid="google-writereview-link"
                  className="flex items-center justify-center gap-2 py-3 rounded-full bg-js-blue text-white text-sm font-semibold hover:bg-js-dark transition-colors duration-300">
                  <Star size={15} /> Leave a Google Review
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-js-charcoal rounded-3xl p-8 md:p-12">
              <h2 className="font-display font-extrabold text-white text-3xl tracking-tight mb-2">Send an enquiry</h2>
              <p className="text-white/60 mb-8">We usually respond within a few hours.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <input required placeholder="Full name" data-testid="contact-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required type="email" placeholder="Email" data-testid="contact-email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input placeholder="Phone" data-testid="contact-phone" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <input placeholder="Company (optional)" data-testid="contact-company" className={inp} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <select data-testid="contact-project-type" className={inp} value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })}>
                    <option value="">Project type</option>
                    <option>Residential</option>
                    <option>Commercial</option>
                    <option>Industrial</option>
                    <option>Educational</option>
                  </select>
                  <input placeholder="Project location / suburb" data-testid="contact-project-location" className={inp} value={form.project_location} onChange={(e) => setForm({ ...form, project_location: e.target.value })} />
                </div>
                <input placeholder="Services required (e.g. VRV, ventilation, ducted)" data-testid="contact-services" className={inp} value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} />
                <select data-testid="contact-preferred" className={inp} value={form.preferred_contact} onChange={(e) => setForm({ ...form, preferred_contact: e.target.value })}>
                  <option value="">Preferred contact method</option>
                  <option>Phone</option>
                  <option>Email</option>
                </select>
                <textarea required placeholder="Project details / how can we help?" rows={5} data-testid="contact-message" className="w-full p-5 rounded-2xl bg-js-grey border border-black/10 focus:border-js-blue focus:ring-2 focus:ring-js-blue/20 outline-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full h-14 rounded-full bg-js-blue text-white font-semibold hover:bg-js-dark transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting && <Loader2 size={18} className="animate-spin" />} Request a Project Quote
                </button>
              </form>
            </div>

            {/* Request a Quote CTA */}
            <div data-testid="contact-quote-cta" className="mt-6 bg-js-blue rounded-3xl p-8 md:p-10 text-white">
              <h3 className="font-display font-extrabold text-2xl tracking-tight">Prefer a detailed quote?</h3>
              <p className="text-white/70 mt-2 leading-relaxed">Tell us about your space and upload a few photos — we'll prepare a free, no-obligation quote tailored to you.</p>
              <Link to="/quote" data-testid="contact-request-quote" className="group mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-js-blue font-semibold hover:bg-js-grey transition-colors duration-300">
                Request a Free Quote <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CtaBanner />
    </div>
  );
};

export default Contact;
